import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ContentContext } from "./ContentProvider";  
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TextToSpeech from "./TextToSpeech";
import EdgeReadAloud from "./EdgeSound";

export default function HtmlViewer() {
    const { id } = useParams();  
    const [raw, setRaw] = useState("");
    const [err, setErr] = useState("");
    const [mode, setMode] = useState("IFRAME");   
    const navigate = useNavigate();
    const { filteredFiles } = React.useContext(ContentContext);

    const pagination = useMemo(() => {
        if (!filteredFiles || filteredFiles.length === 0 || !id) return { prev: null, next: null };

        const currentIndex = filteredFiles.findIndex((f) => f.id === id);
        if (currentIndex === -1) return { prev: null, next: null };

        return {
            prev: filteredFiles[currentIndex - 1] || null,
            next: filteredFiles[currentIndex + 1] || null,
        };
    }, [filteredFiles, id]);

    const relPath = useMemo(() => {
        try {
            return decodeURIComponent(id || "");
        } catch {
            return "";
        }
    }, [id]);

    useEffect(() => {
        setErr("");
        setRaw("");
        if (!relPath) return;

        fetch(`/resumos/${relPath}`)
            .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then(setRaw)
            .catch((e) => setErr(`Erro ao carregar ${relPath}: ${e.message}`));
    }, [relPath]);

    const safeHtml = useMemo(() => DOMPurify.sanitize(raw, { USE_PROFILES: { html: true } }), [raw]);

    if (!id) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                    Selecione um arquivo
                </Typography>
                <Typography color="text.secondary">Use o menu para navegar por pastas, buscar e filtrar.</Typography>
            </Box>
        );
    }

    if (err) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    Falha
                </Typography>
                <Typography color="error.main" sx={{ whiteSpace: "pre-wrap" }}>
                    {err}
                </Typography>
            </Box>
        );
    }




    const goTo = (file) => {
        if (file) navigate(`/doc/${file.id}`);
    };

    return (
        <Box sx={{ p: { xs: 1.5, md: 3 } }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                    <b>Visualizando:</b> {relPath}
                    
                </Typography>

                <Stack direction="row" spacing={1} sx={{display :{ sm: 'none', md: 'block'}}}>
                    <Button
                        size="small"
                        variant={mode === "EMBED" ? "contained" : "outlined"}
                        onClick={() => setMode("EMBED")}
                    >
                        Embed (seguro)
                    </Button>
                    <Button
                        size="small"
                        variant={mode === "IFRAME" ? "contained" : "outlined"}
                        onClick={() => setMode("IFRAME")}
                    >
                        Iframe (HTML inteiro)
                    </Button>
                </Stack>
            </Stack>
 

            {mode === "IFRAME" ? (
                <Box
                    component="iframe"
                    title="doc"
                    src={`/resumos/${relPath}`}
                    sx={{
                        width: "100%",
                        height: { xs: "75vh", md: "82vh" },
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                    }}
                />
            ) : (
                <Box
                    className="htmlDoc"
                    sx={{
                        maxWidth: "100%",
                        overflowX: "hidden",

                        /* Tipografia e quebra */
                        "& *": { maxWidth: "100%" },
                        "& p, & li, & div, & span": {
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                        },

                        /* Imagens e mídias */
                        "& img, & video": {
                            maxWidth: "100%",
                            height: "auto",
                        },

                        /* Iframes e embeds (ex: YouTube) */
                        "& iframe, & embed, & object": {
                            maxWidth: "100%",
                        },

                        /* Código: deixa rolar só o bloco de código */
                        "& pre": {
                            maxWidth: "100%",
                            overflowX: "auto",
                            whiteSpace: "pre-wrap",      // quebra se couber
                            wordBreak: "break-word",     // evita estourar em linhas enormes
                            padding: 1.5,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "divider",
                        },
                        "& code": {
                            wordBreak: "break-word",
                        },

                        /* Tabelas: deixa rolável e evita estourar a página */
                        "& table": {
                            display: "block",
                            width: "100%",
                            maxWidth: "100%",
                            overflowX: "auto",
                            borderCollapse: "collapse",
                        },
                        "& th, & td": {
                            padding: "8px",
                            borderBottom: "1px solid rgba(0,0,0,0.08)",
                            verticalAlign: "top",
                        },

                        /* Alguns HTMLs vêm com width fixo em container */
                        "& [style*='width']": {
                            maxWidth: "100%",
                        },
                    }}
                    dangerouslySetInnerHTML={{ __html: safeHtml }}
                />
            )}


        </Box>


    );
}
